import {
  ArtifactIcon,
  BlueDownloadIcon,
  BlueEcgIcon,
  CloseIcon,
  PendingCommandIcon,
  redInfoIcon,
  WarningAlertIcon,
} from '@/shared/ui/assets';
import {
  NormalButton,
  ConfirmModal,
  PDFViewer,
  AlertBanner,
  CustomButton,
  CustomSelect,
  CustomInput,
  CustomTooltip,
  DiagnosticYieldAlert,
} from '@/shared/ui/components';
import {
  ARTIFACT_REPORT_HOVER_TEXT,
  BIOTRICITY_SUPPORT_ADDRESS,
  STATUS_REPORT_OPTIONS,
} from '@/shared/config';
import { auth } from '@/shared/lib';
import { Alert } from 'antd';
import classNames from 'classnames';
import _ from 'lodash';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';
import EditReportModal from './editReportModal/editReportModal';
import { useInboxDetails } from './useInboxDetails';
import type { InboxDetailsProps } from './inboxDetails.types';
import './inboxDetails.scss';

const InboxDetails = ({
  data: dataProp,
  titleBlockArray,
  handleClickBack,
  updateClickedReport,
  handleClickViewEcgViewer,
  isInstantReport,
  isStudyManagementPage,
  toggleDisplayDrawer,
  updateStatusReport,
  getSuffixIcon,
  hasClosedInstantReportAlert,
  afterCloseInstantReportAlert,
  hasVerifiedReport,
  handleViewVerifiedReportClick: handleViewVerifiedReportClickProp,
  handleAfterReadingReport,
  handleAfterSavingPdfReport,
}: InboxDetailsProps) => {
  const {
    data,
    navigate,
    state,
    setState,
    isNewFormat,
    formatVersion,
    isNewNotiEventReport,
    hasChangedPhysicianInterpretation,
    toggleDisplayEditReportModal,
    downloadPdfReport,
    handleClickAddComment,
    handleClickAddSignature,
    onChangePhysicianInterpretation,
    handleCloseClick,
    handleLeaveClick,
    handleStayClick,
    handleViewInstantReportClick,
    handleViewVerifiedReportClick,
    handleGotItClick,
    currentUrl,
    shouldDisplayDiagnosticYieldAlert,
    shouldDisplayInstantReportAlert,
    isRendering,
    shouldDisplayFindings,
  } = useInboxDetails({
    data: dataProp,
    handleClickBack,
    updateClickedReport,
    isInstantReport,
    hasVerifiedReport,
    handleViewVerifiedReportClick: handleViewVerifiedReportClickProp,
    handleAfterReadingReport,
    handleAfterSavingPdfReport,
  });

  return (
    !_.isEmpty(data) && (
      <>
        <EditReportModal
          isNewFormat={isNewFormat}
          formatVersion={formatVersion}
          isOpen={state.isOpenEditReportModal}
          isSaving={state.isSaving}
          header={state.modalHeader}
          toggle={toggleDisplayEditReportModal}
          physicianInterpretation={state.physicianInterpretation}
          signature={state.signatureBase64String}
          handleClickAddComment={handleClickAddComment}
          handleClickAddSignature={handleClickAddSignature}
          isUploadedAiReport={state.isUploadedAiReport}
          isUploadedArcsReport={state.isUploadedArcsReport}
          isInstantReport={state.isInstantReport}
          isEventReport={state.isEventReport}
          isNotificationReport={state.isNotificationReport}
          isInterimReport={state.isInterimReport}
          profile={data.profile}
          isNewNotiEventReport={isNewNotiEventReport}
        />
        <ConfirmModal
          isOpen={state.openUnsavedChangesFindingsModal}
          title={t`Unsaved changes detected`}
          question={
            shouldDisplayFindings
              ? t`You have unsaved changes in the Findings section. If you leave this page now, your changes will be lost.`
              : t`You have unsaved changes in the Physician interpretation section. If you leave this page now, your changes will be lost.`
          }
          leftButtonName={t`Leave`}
          buttonLeftStyle="danger"
          onClickLeftButton={handleLeaveClick}
          rightButtonName={t`Stay`}
          onClickRightButton={handleStayClick}
        />
        <ConfirmModal
          width={435}
          isOpen={state.openVerifiedReportModal}
          title={t`Switch to the verified report`}
          question={t`You're currently viewing an instant report, which is generated from raw data and may not be fully accurate. The verified End of Use Report for this study is now available. For the most accurate and complete information, we recommend reviewing the verified report.`}
          leftButtonName={t`View Instant Report`}
          onClickLeftButton={handleViewInstantReportClick}
          rightButtonName={t`View Verified Report`}
          onClickRightButton={handleViewVerifiedReportClick}
        />
        <ConfirmModal
          zIndex={1001}
          width={400}
          isOpen={state.openFailedLoadingFontsModal}
          title={t`Unable to complete this action`}
          question={
            <div>
              This action cannot be completed due to a technical issue. Please contact our support
              team at{' '}
              <a className="blue-text" href={`mailto:${BIOTRICITY_SUPPORT_ADDRESS.csMail}`}>
                {BIOTRICITY_SUPPORT_ADDRESS.csMail}
              </a>{' '}
              for assistance.
            </div>
          }
          rightButtonName={t`Got it`}
          onClickRightButton={handleGotItClick}
        />

        <div className="inbox-details">
          <div
            className={classNames('inbox-details__header-container', {
              'has-diagnostic-yield-alert':
                shouldDisplayDiagnosticYieldAlert && !state.hasClosedDiagnosticYieldAlert,
            })}
          >
            <div className="flex items-center">
              <CustomButton
                id="back-btn"
                style="neutral"
                type="secondary"
                size="small"
                icon={<ReactSVG src={CloseIcon} />}
                onClick={handleCloseClick}
                ariaLabel={t`Close`}
              />
              <div className="title-block-container">
                <div className="main-title-container">
                  <span className="main-title">{t`Report`}</span>
                </div>
                {titleBlockArray.map((element) => (
                  <div key={element.title} className="title-block">
                    <div className="title-block__title">{element.title}</div>
                    <div className="flex items-center">
                      {element.title === 'Study ID' || element.title === 'Patient' ? (
                        <div className="title-block__content">
                          <CustomButton
                            id={element.id}
                            className="content button-hyperlink"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDisplayDrawer?.(
                                data,
                                element.title === 'Study ID'
                                  ? t`Study information`
                                  : t`Patient information`,
                              );
                            }}
                          >
                            {element.content as string}
                          </CustomButton>
                        </div>
                      ) : (
                        <div className="title-block__content">{element.content}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center">
              {data.isArtifactReport && (
                <CustomTooltip placement="bottomRight" title={ARTIFACT_REPORT_HOVER_TEXT}>
                  <div className="artifact-box">
                    <img className="artifact-icon" src={ArtifactIcon} alt="Artifact Icon" />
                    <span>{t`Artifact`}</span>
                  </div>
                </CustomTooltip>
              )}
            </div>
          </div>
          <div className="inbox-details__body">
            {shouldDisplayDiagnosticYieldAlert && (
              <DiagnosticYieldAlert
                isInboxTab
                afterClose={() => {
                  setState({ hasClosedDiagnosticYieldAlert: true });
                }}
              />
            )}
            {isRendering && (
              <Alert
                className="error-alert warning"
                message={
                  <div className="text">
                    {t`The report is being refreshed with the latest information. This may take 5-10 minutes. Please wait - all actions are paused until the update is complete.`}
                  </div>
                }
                type="info"
                showIcon
                icon={
                  <img
                    src={PendingCommandIcon}
                    alt="Warning rendering icon"
                    className="rendering-icon"
                  />
                }
              />
            )}
            {hasVerifiedReport ? (
              <div className="verified-report-alert-container">
                <Alert
                  className="error-alert"
                  message={
                    <div className="message-container">
                      <div className="message">
                        {t`You're currently viewing an instant report, which is generated from raw data and may not be fully accurate. The verified End of Use Report for this study is now available. For the most accurate and complete information, we recommend reviewing the verified report.`}
                      </div>
                      <NormalButton
                        id="view-verified-report-btn"
                        className="view-verified-report-btn"
                        buttonName={t`View Verified Report`}
                        isText
                        onClick={handleViewVerifiedReportClickProp}
                      />
                    </div>
                  }
                  type="warning"
                  showIcon
                  icon={<img src={WarningAlertIcon} alt="Warning alert icon" />}
                />
              </div>
            ) : (
              shouldDisplayInstantReportAlert &&
              !hasClosedInstantReportAlert && (
                <Alert
                  className="error-alert"
                  message={
                    <div className="text">
                      {t`This report is automatically generated and not checked for accuracy. Please use at your own discretion.`}
                    </div>
                  }
                  type="error"
                  showIcon
                  icon={<img src={redInfoIcon} alt="Red info icon" />}
                  closable
                  afterClose={afterCloseInstantReportAlert}
                />
              )
            )}
            <div
              className={classNames('content-container', {
                'has-diagnostic-yield-alert':
                  shouldDisplayDiagnosticYieldAlert && !state.hasClosedDiagnosticYieldAlert,
                'has-instant-report-alert':
                  shouldDisplayInstantReportAlert && !hasClosedInstantReportAlert,
              })}
            >
              <div className="left-container">
                <PDFViewer
                  url={state.pdfURL || currentUrl.current}
                  isArtifactReport={data.isArtifactReport}
                />
              </div>
              {!data.isArtifactReport && (
                <div className="right-container">
                  <div className="top-buttons-container">
                    <div className="buttons-container">
                      <CustomButton
                        id="download-btn"
                        size="small"
                        type="outline"
                        style="neutral"
                        icon={<ReactSVG src={BlueDownloadIcon} />}
                        tooltipTitle={t`Download report`}
                        tooltipPlacement="bottom"
                        onClick={downloadPdfReport}
                        disabled={isRendering}
                        ariaLabel={t`Download report`}
                      />
                      {!isInstantReport && data.hasClinicData && (
                        <CustomButton
                          id="view-ecg-btn"
                          type="outline"
                          style="neutral"
                          size="small"
                          tooltipTitle={t`View ECG`}
                          tooltipPlacement="bottom"
                          icon={<ReactSVG src={BlueEcgIcon} />}
                          onClick={() => {
                            handleClickViewEcgViewer();
                          }}
                          disabled={isRendering}
                          ariaLabel={t`View ECG`}
                        />
                      )}
                    </div>
                    {isInstantReport ? (
                      !isStudyManagementPage && (
                        <CustomButton
                          id={`open-study-${data.id}`}
                          size="small"
                          type="outline"
                          onClick={() => {
                            if (data?.study?.id) {
                              navigate(`/studies/${data.study.id}`);
                            }
                          }}
                          disabled={isRendering}
                        >
                          {t`Open study`}
                        </CustomButton>
                      )
                    ) : (
                      <CustomSelect
                        id="status-report-select"
                        className={classNames(
                          data.statusReport === 'Completed'
                            ? data.isSavingStatus || isRendering
                              ? 'bg-completed__saving'
                              : 'bg-completed'
                            : data.isSavingStatus || isRendering
                              ? 'status-report__saving'
                              : 'status-report',
                          {
                            'disabled-select': isRendering,
                          },
                        )}
                        name="status-report"
                        onChange={(name, value) => {
                          updateStatusReport?.(value, data.id);
                        }}
                        value={data.statusReport}
                        disabled={data.isSavingStatus || isRendering}
                        options={STATUS_REPORT_OPTIONS}
                        size="middle"
                        showSearch={false}
                        allowClear={false}
                        placement="bottomRight"
                        suffixIcon={getSuffixIcon?.(data)}
                        aria-label={t`Status report`}
                      />
                    )}
                  </div>
                  {isInstantReport ? (
                    <AlertBanner
                      className="alert-banner-center-icon mt-16"
                      alertType="info"
                      description={
                        auth.roles().includes('Clinic Physician')
                          ? t`Instant reports do not support adding findings or signatures.`
                          : t`Instant reports do not support adding findings.`
                      }
                    />
                  ) : (
                    <div className="findings-container">
                      <span className="title">
                        {shouldDisplayFindings ? t`Findings` : t`Physician interpretation`}
                      </span>
                      <CustomInput
                        id="physician-interpretation-input"
                        aria-label={
                          shouldDisplayFindings ? t`Findings` : t`Physician interpretation`
                        }
                        className="physician-interpretation"
                        placeholder={
                          shouldDisplayFindings ? 'Enter findings...' : 'Enter interpretation...'
                        }
                        type="textarea"
                        value={state.physicianInterpretation}
                        onChange={onChangePhysicianInterpretation}
                        disabled={state.isSaving || isRendering}
                        name="physicianInterpretation"
                      />
                      <div className="findings-container__buttons-container">
                        {auth.roles().includes('Clinic Physician') && (
                          <CustomButton
                            id="sign-and-save-btn"
                            size="small"
                            type="outline"
                            onClick={() => {
                              toggleDisplayEditReportModal('Physician signature');
                            }}
                            disabled={state.isSaving || isRendering}
                          >
                            {t`Sign & Save`}
                          </CustomButton>
                        )}
                        <CustomButton
                          id="save-changes-btn"
                          size="small"
                          disabled={!hasChangedPhysicianInterpretation || isRendering}
                          loading={state.isSaving}
                          onClick={() => {
                            handleClickAddComment(state.physicianInterpretation);
                          }}
                        >
                          {t`Save Changes`}
                        </CustomButton>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </>
    )
  );
};

export default InboxDetails;
